import * as React from "react";
import ReactMarkdown from "react-markdown";
import { FileText, Folder, MessageSquare } from "lucide-react";
import { useToggle } from "react-use";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { FileItem } from "@/types/fse";
import { summaryMarkdownComponents } from "./markdownComponents";
import { SummaryHeader } from "./SummaryHeader";

interface Props {
  files: FileItem[];
  onAskQuestion?: (file: FileItem) => void;
}

interface FileItemRowProps {
  file: FileItem;
  onAskQuestion?: (file: FileItem) => void;
}

const MAX_NAME_LENGTH = 30;

const truncateName = (name: string): string => {
  if (name.length <= MAX_NAME_LENGTH) return name;
  return `${name.slice(0, MAX_NAME_LENGTH)}...`;
};

const FileItemRow: React.FC<FileItemRowProps> = ({ file, onAskQuestion }) => {
  const [isOpen, toggleOpen] = useToggle(false);
  const isTruncated = file.name.length > MAX_NAME_LENGTH;
  const displayName = truncateName(file.name);

  const handleAskQuestion = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAskQuestion?.(file);
  };

  const nameElement = <span className="flex-1 truncate text-left">{displayName}</span>;

  return (
    <div className="rounded-md border bg-white/50 p-3">
      <div className="flex w-full items-center gap-2">
        <button onClick={toggleOpen} className="flex flex-1 items-center gap-2 text-sm font-medium hover:text-primary">
          <FileText className="h-4 w-4 shrink-0" />
          {isTruncated ? (
            <Tooltip text={file.name} side="top" className="max-w-xs bg-gray-900 text-white">
              {nameElement}
            </Tooltip>
          ) : (
            nameElement
          )}
          <span className="shrink-0 text-xs text-muted-foreground">{isOpen ? "Hide" : "Show"}</span>
        </button>
        <Button
          variant="light-beige-secondary"
          size="xs"
          onClick={handleAskQuestion}
          className="gap-1.5 px-2 py-1 text-xs"
        >
          <MessageSquare size={12} />
          Q&A
        </Button>
      </div>
      {isOpen && (
        <div className="mt-3 border-t pt-3">
          <div className="prose prose-sm max-w-none overflow-hidden text-foreground">
            <ReactMarkdown components={summaryMarkdownComponents}>{file.small_summary}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export const ChatFolderEmptyState: React.FC<Props> = ({ files, onAskQuestion }) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto mb-4 w-full max-w-[700px] rounded-lg border border-brand-gold-100 bg-white/70 p-4 duration-300 animate-in fade-in slide-in-from-top-2">
      <SummaryHeader title="Folder Summary" className="mb-3" />
      <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Folder className="h-4 w-4" />
        <span>{files.length} documents</span>
      </div>
      <div className="max-h-[400px] space-y-2 overflow-y-auto">
        {files.map(file => (
          <FileItemRow key={file.id} file={file} onAskQuestion={onAskQuestion} />
        ))}
      </div>
    </div>
  );
};
